/**
 * Step-3 side panels — prototype `openReq` (1105 + the internal-request override 3498), `openEsc` (1178) and
 * `openCommit` (2779). Hosted once in the app frame (`ItemHost`), opened from any page by
 * `?request=` (client or internal request), `?internal_request=`, `?escalation=` or `?client_task=`.
 *
 * Buttons come from capabilities and the item itself (owner, requester), never from a role; the server
 * refuses anything else anyway (404 out of scope, 403 not yours).
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import {
  INTERNAL_REQUEST_STATUS_LABEL,
  INTERNAL_REQUEST_TYPE_LABEL,
  REQUEST_PURPOSE_LABEL,
  type ClientRequest,
  type ClientTask,
  type Escalation,
  type InternalRequest,
  type RequestMessage,
} from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../../api';
import { firstName, todayIso } from '../../lib/format';
import {
  IR_MEANING,
  IR_TONE,
  SEVERITY,
  clientReqStatus,
  dateNeedLabel,
  shortDay,
  slaText,
  useClientRequest,
  useClientTask,
  useEscalation,
  openRequestFile,
  useInternalRequest,
  whenText,
} from '../../lib/requests';
import { useSession } from '../../lib/session';
import { dueText, useTask, useWorkWrite } from '../../lib/work';
import { Drawer, Empty, ErrorState, Loading, errorText, useToast } from '../../ui/kit';
import { ProofBlock } from '../work/TaskPanel';
import { DelayModal, EscalationModal, NotYetModal, ProofModal, TriageModal, useConfirmDone } from './ItemModals';

const linkStyle = { color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' } as const;

/** Prototype `prioDot`: a small ▲ after a high-priority client's name. */
function PrioMark({ priority }: { priority: string }) {
  return priority === 'high' ? (
    <span className="pm" title="High priority client">
      {' '}▲
    </span>
  ) : null;
}

/** Every side-panel key in the address. Only one panel is open at a time. */
const PANEL_KEYS = ['task', 'request', 'internal_request', 'escalation', 'client_task'] as const;
type PanelKey = (typeof PANEL_KEYS)[number];

/** The current address with only the open panel swapped, so the page's own filters (status, type, client…) stay. */
export function panelSearch(search: string, key: PanelKey, id: string): string {
  const n = new URLSearchParams(search);
  for (const k of PANEL_KEYS) n.delete(k);
  n.set(key, id);
  return `?${n.toString()}`;
}

/** A link from one panel to another item's panel, on the same page. */
function PanelLink({ to: [key, id], children, className, style }: { to: [PanelKey, string]; children: ReactNode; className?: string; style?: CSSProperties }) {
  const loc = useLocation();
  return (
    <Link to={{ pathname: loc.pathname, search: panelSearch(loc.search, key, id) }} className={className} style={style ?? linkStyle}>
      {children}
    </Link>
  );
}

function Lbl({ children, first }: { children: ReactNode; first?: boolean }) {
  return (
    <div className="lbl" style={{ margin: first ? '0 0 8px' : '20px 0 8px' }}>
      {children}
    </div>
  );
}

function NotOpen({ what }: { what: string }) {
  return <Empty title={`This ${what} is not open to you`}>It may have been closed, or it belongs to someone else’s client.</Empty>;
}

function Body<T>({ q, what, children }: { q: { isLoading: boolean; error: unknown; data: T | undefined; refetch: () => unknown }; what: string; children: (d: T) => ReactNode }) {
  if (q.isLoading) return <Loading />;
  if (q.error) {
    if (q.error instanceof ApiFailure && (q.error.status === 404 || q.error.status === 403)) return <NotOpen what={what} />;
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  return q.data ? <>{children(q.data)}</> : null;
}

// ---------------------------------------------------------------- host

/** Opens the step-3 panel named in the address, over whatever page the person is on. */
export function ItemHost() {
  const [sp, setSp] = useSearchParams();
  const keys = ['request', 'internal_request', 'escalation', 'client_task'] as const;
  const k = keys.find((x) => sp.get(x));
  if (!k) return null;
  const id = sp.get(k)!;
  const close = () => {
    const n = new URLSearchParams(sp);
    n.delete(k);
    setSp(n);
  };
  if (k === 'escalation') return <EscalationPanel key={id} id={id} onClose={close} />;
  if (k === 'client_task') return <ClientTaskPanel key={id} id={id} onClose={close} />;
  if (k === 'internal_request') return <RequestPanel key={id} id={id} kind="internal" onClose={close} />;
  return <RequestPanel key={id} id={id} kind="either" onClose={close} />;
}

// ---------------------------------------------------------------- request (client or internal)

/**
 * `?request=` is shared by client and internal requests (both UUIDs). People who can read clients try the client
 * request first and fall back to the internal one on 404; a requester only ever has internal requests.
 */
export function RequestPanel({ id, kind, onClose }: { id: string; kind: 'either' | 'internal'; onClose: () => void }) {
  const { can } = useSession();
  const tryClient = kind === 'either' && (can('clients.read.all') || can('clients.read.own'));
  const cq = useClientRequest(id, tryClient);
  const clientMissing = !tryClient || (cq.error instanceof ApiFailure && (cq.error.status === 404 || cq.error.status === 403));
  const iq = useInternalRequest(id, clientMissing);
  const [modal, setModal] = useState<null | 'proof' | 'escalate' | 'triage' | 'delay' | 'notyet'>(null);

  let body: ReactNode;
  if (tryClient && cq.isLoading) body = <Loading />;
  else if (!clientMissing && cq.error) body = <ErrorState error={cq.error} onRetry={() => void cq.refetch()} />;
  else if (cq.data && !clientMissing) body = <ClientRequestBody r={cq.data} open={setModal} />;
  else body = <Body q={iq} what="request">{(r) => <InternalRequestBody r={r} open={setModal} />}</Body>;

  const cr = !clientMissing ? cq.data : undefined;
  const ir = clientMissing ? iq.data : undefined;
  const shut = () => setModal(null);
  return (
    <>
      <Drawer title="Request" onClose={onClose}>
        {body}
      </Drawer>
      {cr && modal === 'proof' ? (
        <ProofModal
          noun="request"
          title={cr.text}
          onClose={shut}
          submit={(b) => call('clientRequests.close', { params: { id: cr.id }, idempotencyKey: newIdempotencyKey(), ifMatch: cr.version, body: b })}
        />
      ) : null}
      {cr && modal === 'escalate' ? <EscalationModal onClose={shut} from={{ requestId: cr.id, text: cr.text, clientId: cr.client.id }} /> : null}
      {ir && modal === 'proof' ? (
        <ProofModal
          noun="request"
          title={ir.text}
          onClose={shut}
          submit={(b) => call('internalRequests.close', { params: { id: ir.id }, idempotencyKey: newIdempotencyKey(), ifMatch: ir.version, body: { ...b, note: b.proof_text ?? 'Done' } })}
        />
      ) : null}
      {ir && modal === 'triage' ? <TriageModal r={ir} onClose={shut} /> : null}
      {ir && modal === 'delay' ? <DelayModal r={ir} onClose={shut} /> : null}
      {ir && modal === 'notyet' ? <NotYetModal r={ir} onClose={shut} /> : null}
    </>
  );
}

/**
 * Prototype `openReq` Conversation: left-bordered entries (first name, when, text), oldest first, or "No messages yet."
 * Shared by client requests (an Ops-only thread) and internal requests (which also carry internal notes and system entries).
 */
function Conversation({ messages }: { messages: RequestMessage[] }) {
  return (
    <>
      <Lbl>Conversation</Lbl>
      <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
        {messages.length ? (
          messages.map((m) => (
            <div key={m.id} style={{ borderLeft: '2px solid var(--brand-line)', paddingLeft: 12, fontSize: 13.5, color: m.kind === 'system' ? 'var(--sub)' : undefined }}>
              {m.by ? <b>{firstName(m.by.name)}</b> : null} <span className="sub" style={{ fontSize: 12 }}>{whenText(m.at)}</span>
              {m.is_internal_note ? (
                <span className="chip" style={{ marginLeft: 6 }}>
                  Internal note
                </span>
              ) : null}
              <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.body}</div>
            </div>
          ))
        ) : (
          <div className="sub">No messages yet.</div>
        )}
      </div>
    </>
  );
}

type Open = (m: 'proof' | 'escalate' | 'triage' | 'delay' | 'notyet') => void;

function ClientRequestBody({ r, open }: { r: ClientRequest; open: Open }) {
  const { me, can, readOnly } = useSession();
  const toast = useToast();
  const st = clientReqStatus(r.status);
  const live = r.status !== 'done';
  const mgr = can('tasks.read.all');
  const ops = !readOnly && (mgr || r.owner?.id === me.user.id || r.client.spoc.id === me.user.id);
  const c = r.clock.value;
  const toTask = useWorkWrite(() => call('clientRequests.toTask', { params: { id: r.id }, idempotencyKey: newIdempotencyKey(), body: {} }));
  const [msg, setMsg] = useState('');
  const send = useWorkWrite(() => call('clientRequests.message', { params: { id: r.id }, idempotencyKey: newIdempotencyKey(), body: { body: msg.trim() } }));
  const post = () => {
    if (!msg.trim()) return toast('Write a message first');
    send.mutate(undefined, {
      onSuccess: () => {
        setMsg('');
        toast('Sent');
      },
      onError: (e) => toast(errorText(e)),
    });
  };
  const makeTask = () =>
    toTask.mutate(undefined, {
      onSuccess: (t) => toast(`Task created for ${t.assignee ? firstName(t.assignee.name) : 'the owner'}`),
      onError: (e) => toast(errorText(e)),
    });
  return (
    <>
      <h2 style={{ fontSize: 18 }}>{r.text}</h2>
      <div style={{ margin: '8px 0 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <span className={`chip ${st.tone}`}>{st.label}</span>
        {live ? <span className={`chip ${c.is_breached ? 'cold' : ''}`}>{slaText(c.hours_left)}</span> : null}
        {r.escalation_id ? (
          <PanelLink to={['escalation', r.escalation_id]} className="chip cold" style={{ textDecoration: 'none' }}>
            Escalated
          </PanelLink>
        ) : null}
      </div>
      <dl className="kv">
        <dt>From</dt>
        <dd>Client</dd>
        <dt>Type</dt>
        <dd>Client request</dd>
        <dt>Client</dt>
        <dd>
          <Link to={`/clients/book?client=${r.client.id}`} style={linkStyle}>
            {r.client.name}
          </Link>
          <PrioMark priority={r.client.priority} />
        </dd>
        <dt>Owner</dt>
        <dd>{r.owner ? r.owner.name : 'Not assigned yet'}</dd>
        <dt>Came in by</dt>
        <dd>
          {r.via}, {whenText(r.received_at)}
        </dd>
        <dt>Reply by</dt>
        <dd>{whenText(r.respond_by_at)}</dd>
        {r.task_ids.length ? (
          <>
            <dt>Task</dt>
            <dd style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {r.task_ids.map((t, i) => (
                <PanelLink key={t} to={['task', t]}>
                  {r.task_ids.length > 1 ? `Task ${i + 1}` : 'Open the task'}
                </PanelLink>
              ))}
            </dd>
          </>
        ) : null}
      </dl>
      {r.closure ? (
        <>
          <Lbl>Proof</Lbl>
          <ProofBlock closure={r.closure} />
        </>
      ) : null}
      <Conversation messages={r.messages ?? []} />
      {ops && live ? (
        <>
          <textarea className="in" rows={2} placeholder="Add a message" value={msg} onChange={(e) => setMsg(e.target.value)} aria-label="Add a message" maxLength={2000} />
          <div className="split" style={{ marginTop: 8, flexWrap: 'wrap' }}>
            <button className="btn sm" onClick={post} disabled={send.isPending} aria-busy={send.isPending}>
              Send message
            </button>
            <div className="grow" />
            <button className="btn sm" onClick={makeTask} disabled={toTask.isPending} aria-busy={toTask.isPending}>
              Make task
            </button>
            {!r.escalation_id ? (
              <button className="btn sm bad" onClick={() => open('escalate')}>
                Escalate
              </button>
            ) : null}
            <button className="btn sm good" onClick={() => open('proof')}>
              Mark done
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}

const TEAM = (t: 'sales' | 'marketing') => (t === 'sales' ? 'Sales' : 'Marketing');

function InternalRequestBody({ r, open }: { r: InternalRequest; open: Open }) {
  const { me, can, readOnly } = useSession();
  const toast = useToast();
  const today = todayIso();
  const requester = r.by.id === me.user.id;
  const mgr = can('requests.triage');
  const ops = !readOnly && (mgr || r.owner?.id === me.user.id);
  const c = r.clock.value;
  const live = r.status !== 'done' && r.status !== 'declined';
  const [msg, setMsg] = useState('');
  const send = useWorkWrite(() => call('internalRequests.message', { params: { id: r.id }, idempotencyKey: newIdempotencyKey(), body: { body: msg.trim() } }));
  const confirm = useConfirmDone();
  const post = () => {
    if (!msg.trim()) return toast('Write a message first');
    send.mutate(undefined, {
      onSuccess: () => {
        setMsg('');
        toast('Sent');
      },
      onError: (e) => toast(errorText(e)),
    });
  };
  const late = !!r.expected_by && r.expected_by > r.needed_by;
  const about = r.client ? (requester ? r.client.name : <Link to={`/clients/book?client=${r.client.id}`} style={linkStyle}>{r.client.name}</Link>) : r.prospect || 'None';

  return (
    <>
      <h2 style={{ fontSize: 18 }}>{r.text}</h2>
      <div style={{ margin: '8px 0 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <span className={`chip ${IR_TONE[r.status]}`}>{INTERNAL_REQUEST_STATUS_LABEL[r.status]}</span>
        {live && c.paused ? (
          <span className="chip" title={r.clock.explain[0]?.detail}>
            Paused
          </span>
        ) : live && c.hours_to_ack != null ? (
          <span className={`chip ${c.is_breached ? 'cold' : ''}`}>{slaText(c.hours_to_ack)}</span>
        ) : null}
      </div>
      <dl className="kv">
        <dt>From</dt>
        <dd>
          {r.by.name}, {TEAM(r.team)}
          {r.logged_by && r.logged_by.id !== r.by.id ? <span className="sub"> (logged by {firstName(r.logged_by.name)})</span> : null}
        </dd>
        <dt>Type</dt>
        <dd>{INTERNAL_REQUEST_TYPE_LABEL[r.type].label}</dd>
        <dt>About</dt>
        <dd>{about}</dd>
        <dt>Owner</dt>
        <dd>{r.owner ? r.owner.name : 'Not assigned yet'}</dd>
        <dt>For</dt>
        <dd>{REQUEST_PURPOSE_LABEL[r.purpose]}</dd>
        <dt>Needed by</dt>
        <dd>{dateNeedLabel(r.needed_by, today)}</dd>
        <dt>Why it matters</dt>
        <dd>{r.why_it_matters}</dd>
        {r.files.length ? (
          <>
            <dt>Files</dt>
            <dd>
              {r.files.map((f, n) => (
                <button
                  key={f.key}
                  type="button"
                  className="chip info"
                  style={{ marginRight: 4, cursor: 'pointer' }}
                  title="Open (the link lasts 10 minutes)"
                  onClick={() => openRequestFile(r.id, n).catch((e) => toast(errorText(e)))}
                >
                  {f.name}
                </button>
              ))}
            </dd>
          </>
        ) : null}
        <dt>Acknowledged</dt>
        <dd>
          {r.acked_at ? (
            `${r.acked_by ? `${r.acked_by.name}, ` : ''}${whenText(r.acked_at)}`
          ) : (
            <span className="chip warm">Not yet. Ops replies within 24 hours</span>
          )}
        </dd>
        {r.expected_by ? (
          <>
            <dt>Expected by</dt>
            <dd>
              {dateNeedLabel(r.expected_by, today)} {late ? <span className="chip cold">Later than you needed</span> : null}
              {r.expected_why ? (
                <div className="sub" style={{ marginTop: 3 }}>
                  {r.expected_why}
                </div>
              ) : null}
            </dd>
          </>
        ) : null}
        {r.task_id && !requester ? (
          <>
            <dt>Task</dt>
            <dd>
              <PanelLink to={['task', r.task_id]}>
                Open the task
              </PanelLink>
            </dd>
          </>
        ) : null}
      </dl>

      <div className="note" style={{ margin: '0 0 14px' }}>
        <b>{INTERNAL_REQUEST_STATUS_LABEL[r.status]}.</b> {IR_MEANING[r.status]}
        {r.status === 'waiting_triage' && c.is_breached ? <b> Past 24 hours, so it has been flagged to the Ops associate director.</b> : null}
      </div>

      {r.status === 'waiting_confirm' && requester && !readOnly ? (
        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="bd">
            <b>Is this what you needed?</b>
            {r.closure?.note || r.closure?.proof_text ? (
              <div className="sub" style={{ margin: '4px 0 10px' }}>
                {firstName(r.closure.closed_by.name)} says: {r.closure.note ?? r.closure.proof_text}
              </div>
            ) : (
              <div style={{ height: 10 }} />
            )}
            {r.closure?.proof_url ? (
              <a href={r.closure.proof_url} target="_blank" rel="noreferrer" className="chip info" style={{ textDecoration: 'none', marginBottom: 10, display: 'inline-flex' }}>
                {r.closure.proof_url.replace(/^https?:\/\//, '').slice(0, 60)}
              </a>
            ) : null}
            <div className="split">
              <button className="btn good" onClick={() => confirm.confirm(r.id)} disabled={confirm.busy} aria-busy={confirm.busy}>
                Yes, it is done
              </button>
              <button className="btn" onClick={() => open('notyet')}>
                Not quite yet
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {r.closure && !requester ? (
        <>
          <Lbl first>Proof</Lbl>
          <ProofBlock closure={r.closure} />
        </>
      ) : null}

      <Conversation messages={r.messages ?? []} />
      {live && !readOnly && (requester || ops) ? (
        <>
          <textarea className="in" rows={2} placeholder="Add a message" value={msg} onChange={(e) => setMsg(e.target.value)} aria-label="Add a message" />
          <div className="split" style={{ marginTop: 8, flexWrap: 'wrap' }}>
            <button className="btn sm" onClick={post} disabled={send.isPending} aria-busy={send.isPending}>
              Send message
            </button>
            <div className="grow" />
            {ops && r.status === 'waiting_triage' && mgr ? (
              <button className="btn sm pri" onClick={() => open('triage')}>
                Triage
              </button>
            ) : null}
            {ops && r.status === 'in_progress' ? (
              <>
                <button className="btn sm" onClick={() => open('delay')}>
                  Running late
                </button>
                <button className="btn sm good" onClick={() => open('proof')}>
                  Mark done
                </button>
              </>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- escalation

export function EscalationPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useEscalation(id);
  const [proof, setProof] = useState(false);
  const e = q.data;
  return (
    <>
      <Drawer title="Escalation" onClose={onClose}>
        <Body q={q} what="escalation">
          {(x) => <EscalationBody e={x} onClose={() => setProof(true)} />}
        </Body>
      </Drawer>
      {e && proof ? (
        <ProofModal
          noun="escalation"
          title={e.title}
          onClose={() => setProof(false)}
          submit={(b) => call('escalations.close', { params: { id: e.id }, idempotencyKey: newIdempotencyKey(), ifMatch: e.version, body: b })}
        />
      ) : null}
    </>
  );
}

function EscalationBody({ e, onClose }: { e: Escalation; onClose: () => void }) {
  const { me, can, readOnly } = useSession();
  const toast = useToast();
  const sev = SEVERITY[e.severity];
  const [note, setNote] = useState('');
  const may = !readOnly && (can('tasks.read.all') || e.owner?.id === me.user.id || e.client.spoc.id === me.user.id);
  const add = useWorkWrite(() => call('escalations.note', { params: { id: e.id }, idempotencyKey: newIdempotencyKey(), body: { body: note.trim() } }));
  const go = () => {
    if (!note.trim()) return toast('Write the update first');
    add.mutate(undefined, {
      onSuccess: () => {
        setNote('');
        toast('Update added');
      },
      onError: (x) => toast(errorText(x)),
    });
  };
  return (
    <>
      <h2 style={{ fontSize: 18 }}>{e.title}</h2>
      <div style={{ margin: '8px 0 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <span className={`chip ${sev.tone}`}>{sev.label}</span>
        <span className="chip">{e.client.name}</span>
        {e.status === 'closed' ? <span className="chip hot">Closed{e.closed_on ? ` ${shortDay(e.closed_on)}` : ''}</span> : null}
        {e.client_request_id ? (
          <PanelLink to={['request', e.client_request_id]} className="chip" style={{ textDecoration: 'none' }}>
            From a client request
          </PanelLink>
        ) : null}
      </div>
      <Lbl first>Timeline</Lbl>
      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        {e.notes.length ? (
          e.notes.map((n) => (
            <div key={n.id} style={{ borderLeft: '2px solid var(--brand-line)', paddingLeft: 12, fontSize: 13.5 }}>
              {shortDay(n.at.slice(0, 10))} {whenText(n.at).split(' ').slice(-2).join(' ')}: {n.body}
              {/* The server's first note already names the person ("Raised by {name}"); do not repeat it. */}
              {n.by && !n.body.includes(firstName(n.by.name)) ? <span className="sub"> – {firstName(n.by.name)}</span> : null}
            </div>
          ))
        ) : (
          // The server writes the dated first note ("Raised by …" / "Promoted from a client request by …").
          <div className="sub">No notes yet.</div>
        )}
      </div>
      {e.closure ? (
        <>
          <Lbl first>Proof</Lbl>
          <ProofBlock closure={e.closure} />
        </>
      ) : null}
      {may && e.status === 'open' ? (
        <>
          <label className="fld" style={{ marginTop: 12 }}>
            <span>Add an update</span>
            <textarea className="in" value={note} onChange={(x) => setNote(x.target.value)} />
          </label>
          <div className="split">
            <button className="btn" onClick={go} disabled={add.isPending} aria-busy={add.isPending}>
              Add update
            </button>
            <div className="grow" />
            <button className="btn good" onClick={onClose}>
              Close escalation
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- client task

export function ClientTaskPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useClientTask(id);
  const [proof, setProof] = useState(false);
  const m = q.data;
  return (
    <>
      <Drawer title="Task" onClose={onClose}>
        <Body q={q} what="task">
          {(x) => <ClientTaskBody m={x} onKept={() => setProof(true)} onClose={onClose} />}
        </Body>
      </Drawer>
      {m && proof ? (
        <ProofModal
          noun="task"
          title={m.what}
          onClose={() => setProof(false)}
          submit={(b) => call('clientTasks.kept', { params: { id: m.id }, idempotencyKey: newIdempotencyKey(), ifMatch: m.version, body: b })}
        />
      ) : null}
    </>
  );
}

/** Prototype `cmClock()`: kept → "Kept", else the due chip. */
export function clientTaskChip(m: { status: string; due: string | null }, today = todayIso()): { tone: string; label: string } {
  if (m.status === 'kept') return { tone: 'hot', label: 'Kept' };
  if (m.status === 'dropped') return { tone: '', label: 'Dropped' };
  if (!m.due) return { tone: '', label: 'No date' };
  return dueText(m.due, today);
}

function ClientTaskBody({ m, onKept, onClose }: { m: ClientTask; onKept: () => void; onClose: () => void }) {
  // Prototype openCommit: the Task row links with the linked task's title.
  const linked = useTask(m.task_id);
  const { me, can, readOnly } = useSession();
  const chip = clientTaskChip(m);
  const may = !readOnly && (can('tasks.read.all') || m.promised_by.id === me.user.id || m.client.spoc.id === me.user.id);
  return (
    <>
      <h2 style={{ fontSize: 18, marginBottom: 10 }}>{m.what}</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <span className={`chip ${chip.tone}`}>{chip.label}</span>
      </div>
      <dl className="kv">
        <dt>Client</dt>
        <dd>
          <Link to={`/clients/book?client=${m.client.id}`} style={linkStyle}>
            {m.client.name}
          </Link>
          <PrioMark priority={m.client.priority} />
        </dd>
        <dt>With</dt>
        <dd>{m.promised_by.name}</dd>
        <dt>For</dt>
        <dd>{m.promised_to}</dd>
        <dt>Where it came from</dt>
        <dd>{m.source ?? (m.origin_ref ? m.origin_ref.label : 'Logged by hand')}</dd>
        {m.task_id ? (
          <>
            <dt>Task</dt>
            <dd>
              <PanelLink to={['task', m.task_id]}>
                {linked.data?.title ?? 'Open the task'}
              </PanelLink>
            </dd>
          </>
        ) : null}
      </dl>
      {m.closure ? (
        <>
          <Lbl>Proof</Lbl>
          <ProofBlock closure={m.closure} />
        </>
      ) : null}
      <div className="split" style={{ marginTop: 22 }}>
        {may && m.status === 'open' ? (
          <button className="btn good" onClick={onKept}>
            Mark kept, with proof
          </button>
        ) : null}
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </div>
    </>
  );
}
